import { economyLink } from "../hub/economyLink";
import type { OfferView, PageView } from "@highmarch/app";
import { BalanceData, GearItem, GrantedItems, OfferItem, offerOf, pageOf } from "@highmarch/domain";
import { bindSeg, fmtCompact } from "../kit";
import { currencyLineHtml, emptyHtml, iconTabsHtml, sheetHeadHtml, tickTimers } from "../components";
import { toast } from "../toast";
import { currencyIconUrl, gemIcon } from "../hub/econArt";
import { uiUrl } from "../portraits";
import { confirmPurchase, econCue, flyCurrency, purchasePop } from "../hub/purchaseFx";
import { DealsLink, dealsLink, extraTab, extraTabViews } from "./dealsLink";
import { TileNames, blockedText, fmtCountdown, initialTab, itemTiles, needsChoice, priceText, purchaseFailText, tabsFor } from "./dealsModel";
import { PageCtx, gemPackCtx, pageHtml } from "./dealsPages";
import { wellsOf } from "./dealsTiles";
import { bindItemFocus, chooseGearSlot, choosePick, chooseTheme, showOdds } from "./pickSheet";
import { openVip, vipChipFacts, vipChipHtml } from "./vipSheet";
import { revealThenReceipt } from "./receipt";
import { SHOP_SKIN } from "../shopSkin";
import { withCardTabs } from "./cardsModel";
import { bindCardsPage, cardsCtx, cardsPageShown } from "./cardsActions";
import { suggestedPage, suggestedPageShown, withSuggestedTabs } from "./suggestTabs";
import { bindPackPopups } from "./packPopup";
import { showActiveTab } from "../shops/tabStrip";

/**
 * The Deals screen (docs/SHOP.md §5.2; Kingshot's "Deals" density in the game's "Keep & Plate" look): a parchment
 * hub sheet with the standard header (the deals chest, the VIP chip and the gems pill), the icon-tab strip of pages,
 * and the page — its painted hero, a real countdown, and the offers with their honest value, VIP XP and limits. Opened only by a tap in the hub (the Deals pill, the gems "+", More → VIP): never from a level, the end
 * screen or a timer (the quiet rules). Purchases go through the link (prepare → store → grant once → save → finish).
 */

/** The Store's Gold tab (not a Deals page: gold packs are bought with gems). */
/** How often the countdowns tick. */
const TICK_MS = 1000;
/** How long a bought card's glow plays before the page redraws it. */
const REVEAL_MS = 700;

/**
 * The Store's tab order (owner 2026-10-21): the strongest pages first — First Command while it is on sale (and the
 * Welcome Back pack), Daily Deals, Cards, the Pass — then Weekly, Builders and Gems, then the ladder pages. A page not
 * named keeps its data order after these.
 */
const STORE_ORDER = ["starter", "welcome", "daily", "cards", "x:pass", "weekly", "builders", "gems", "commander", "chapter", "fund", "custom", "masters", "armoury", "treasury"];
function storeOrder<T extends { key: string }>(tabs: readonly T[]): T[] {
  const at = (k: string) => { const i = STORE_ORDER.indexOf(k); return i < 0 ? STORE_ORDER.length : i; };
  return tabs.map((t, i) => ({ t, i })).sort((a, b) => at(a.t.key) - at(b.t.key) || a.i - b.i).map((x) => x.t);
}

export interface OpenDealsOptions {
  /** The page to open on (the gems "+" opens "gems"); the first page when it is not on the strip. */
  readonly page?: string | null;
}

/** Opens the Deals screen, or returns null when there is nothing to show here (no store, not in the hub). */
export function openDeals(opts: OpenDealsOptions = {}): HTMLElement | null {
  const l = dealsLink();
  if (!l || !l.hub) return null;                   // the hub only: never a level or the end screen
  if (l.deals.pages(l.now(), l.ctx()).length === 0) return null;
  const el = new DealsScreen(l, opts.page ?? null).el;
  // MP-04: opening Deals clears the pill's "new offer" badge (First Command after level 3).
  if (l.deals.noteOpened(l.now(), l.ctx())) l.changed();
  return el;
}

class DealsScreen {
  readonly el: HTMLDivElement;
  private page: string | null;
  private prices: ReadonlyMap<string, string> = new Map();
  private timer = 0;
  private busy = false;
  /** A ladder page's tier on show, by page (unset: the first still for sale). */
  private readonly ladderAt = new Map<string, number>();

  constructor(private readonly l: DealsLink, page: string | null) {
    this.page = page;
    this.el = document.createElement("div");
    this.el.className = `sheet panel deals ${SHOP_SKIN}`;
    this.render();
    l.hub!.parent.append(this.el);
    this.scrollTabIntoView(false);
    this.timer = window.setInterval(() => this.tick(), TICK_MS);
    void l.prices().then((p) => { this.prices = p; if (this.el.isConnected) this.render(); }).catch(() => undefined);
  }

  private get balance(): BalanceData { return this.l.catalog.balance; }
  private get names(): TileNames { return { skin: skinName }; }

  private pages(): PageView[] { return this.l.deals.pages(this.l.now(), this.l.ctx()); }

  private render(): void {
    const now = this.l.now(), pages = this.pages(), k = cardsCtx(this.l), base = withCardTabs(tabsFor(pages), k.cards, k.fund);
    // MP-05: with no page asked for, Deals opens on a suggested page; drawing a page clears its suggestion (its dot).
    const xpage = this.page ? extraTab(this.page) : null;   // a feature's page drawn inside the Store (the Pass)
    if (!xpage?.mount) this.page = initialTab(storeOrder(base.map((t) => ({ ...t, key: t.id }))), this.page ?? suggestedPage(this.l, base));
    suggestedPageShown(this.l, this.page);
    const tabs = withSuggestedTabs(base, this.l);
    let p = pages.find((x) => x.page.id === this.page) ?? null;
    // owner 2026-10-21: an unchosen Master's prize or Custom Chest slot is pre-picked (the best worth) so the price shows
    if (p && p.offers.some((o) => (o.prizes.length > 0 && o.prize < 0) || o.picks.some((x) => x < 0))) {
      let changed = false;
      for (const o of p.offers) changed = this.l.deals.prefill(o.offerId, now, this.l.ctx()) || changed;
      if (changed) { this.l.changed(); p = this.pages().find((x) => x.page.id === this.page) ?? null; }
    }
    const scroller = this.el.querySelector<HTMLElement>(".dscroll"), strip = this.el.querySelector<HTMLElement>(".k-itabs");
    const keep = scroller && this.el.dataset.page === this.page ? scroller.scrollTop : 0, stripX = strip?.scrollLeft ?? 0;
    const chip = vipChipFacts(this.l);
    this.el.dataset.page = this.page ?? "";
    // The icon tabs keep the pass-1 hooks (`.dtab[data-page]`: the smoke test and the capture sweeps click them).
    // Shop redesign (owner 2026-10-19): the Store — every Deals page, then Gold (gold packs for gems) and the extra tabs.
    const strip2 = iconTabsHtml(storeOrder([...tabs.map((t) => ({ key: t.id, label: t.label, icon: uiUrl(t.icon), dot: t.dot, className: "dtab", data: `data-page="${t.id}"` })), ...extraTabViews()]), this.page ?? "", "Store pages");
    const body = xpage?.mount ? `<div class="dxhost"></div>` : p ? pageHtml(p, this.ctx(now)) : emptyHtml(uiUrl("icons/deals_starter.png"), "No deals right now.");
    this.el.innerHTML = sheetHeadHtml({
      title: "Store", sub: currencyLineHtml([{ key: "gems", icon: currencyIconUrl("gems"), amount: fmtCompact(this.l.catalog.economy.wallet.gems) }]), portrait: uiUrl("icons/deals_treasury.png"), tint: "navy",
      extra: vipChipHtml(chip.level, chip.crest, chip.dot > 0),
    }) + `<div class="dtabs">${strip2}</div>
      <div class="dscroll">${body}</div>`;
    const ns = this.el.querySelector<HTMLElement>(".dscroll"), nt = this.el.querySelector<HTMLElement>(".k-itabs");
    if (xpage?.mount) { const h = this.el.querySelector<HTMLElement>(".dxhost"); if (h) xpage.mount(h); }
    if (ns) ns.scrollTop = keep;
    if (nt) nt.scrollLeft = stripX;
    this.bind(xpage?.mount ? null : p);
    cardsPageShown(this.l, this.page);
  }

  private ctx(now: number): PageCtx {
    const d = this.l.deals;
    return {
      ladderAt: this.page ? this.ladderAt.get(this.page) : undefined,
      balance: this.balance, level: d.level, now, prices: this.prices, names: this.names, sandbox: this.l.storeKind === "sandbox", pageTitle: this.pageTitle(),
      gemPacks: gemPackCtx(this.l.gemPacks(), (items, cents) => d.value(items, cents), (cents) => d.vipPointsFor(cents), (id) => economyLink()?.purchases?.firstBonus(id) ?? false), cards: cardsCtx(this.l),
    };
  }

  private pageTitle(): string { return this.balance.offers.pages.find((x) => x.id === this.page)?.title ?? ""; }

  /** The lit tab in view: the strip scrolls sideways only, never the sheet (tabStrip.ts). */
  private scrollTabIntoView(smooth: boolean): void { showActiveTab(this.el, smooth); }

  /** The countdowns tick each second; a page whose period ended redraws (new limits, a new Daily theme). */
  private tick(): void {
    if (!this.el.isConnected) { window.clearInterval(this.timer); return; }
    if (tickTimers(this.el, this.l.now(), fmtCountdown) && !this.busy) this.render();
  }

  private view(offerId: string): OfferView | null { return this.l.deals.view(offerId, this.l.now(), this.l.ctx()); }
  private get parent(): HTMLElement { return this.l.hub?.parent ?? this.el.parentElement ?? document.body; }

  private bind(p: PageView | null): void {
    const q = <T extends HTMLElement>(s: string) => [...this.el.querySelectorAll<T>(s)];
    this.el.querySelector<HTMLButtonElement>(".k-head .close")!.onclick = () => this.close();
    this.el.querySelector<HTMLButtonElement>(".dvipchip")!.onclick = () => { openVip(); };
    bindSeg(this.el, (key) => {
      const x = extraTab(key);   // another feature's screen (the War Campaign pass): Deals closes, that screen opens
      if (x && !x.mount) { econCue("tap"); this.close(); x.open(); } else if (key !== this.page) { this.page = key; econCue("tap"); this.render(); this.scrollTabIntoView(true); }
    });
    if (!p) return;
    for (const b of q<HTMLButtonElement>(".dlstep")) b.onclick = () => {
      if (b.classList.contains("off")) return;
      const at = Number(b.closest<HTMLElement>(".dladder")?.dataset.at ?? 0) + Number(b.dataset.step);
      this.ladderAt.set(p.page.id, Math.max(0, Math.min(p.offers.length - 1, at)));
      econCue("tap");
      this.render();
    };
    for (const b of q<HTMLButtonElement>(".dbuy[data-offer]")) b.onclick = () => void this.buy(b.dataset.offer!, b);
    for (const b of q<HTMLButtonElement>(".dbuy.gbuy")) b.onclick = () => void this.buyGems(b.dataset.pack!, b);
    for (const b of q<HTMLButtonElement>(".doffer.custom .dplus, .doffer.custom .picked")) b.onclick = (e) => { e.stopPropagation(); void this.pick(b.closest<HTMLElement>(".doffer")!.dataset.offer!, Number(b.dataset.slot)); };
    for (const b of q<HTMLButtonElement>(".doffer.masters .prize")) b.onclick = () => void this.prize(b.closest<HTMLElement>(".doffer")!.dataset.offer!, Number(b.dataset.prize));
    for (const b of q<HTMLButtonElement>(".dchange")) b.onclick = () => void this.changeTheme(p);
    for (const b of q<HTMLButtonElement>(".tstop")) b.onclick = () => this.milestone(Number(b.dataset.tokens), b);
    // a tap on an item brings it into focus with its odds (owner 2026-10-21: no "?" badges, no ODDS buttons)
    for (const b of q<HTMLButtonElement>("button.dwell:not(.picked)")) b.onclick = (e) => { e.stopPropagation(); this.info(b); };
    bindItemFocus(this.el, this.parent, this.balance, this.l.deals.level, this.names);
    bindCardsPage(this.el, this.l, this.parent, () => this.render());
    // owner 2026-10-24: a tap on a pack opens what it holds, with BUY (packPopup.ts)
    bindPackPopups(this.el, this.parent, { buy: (id, b) => void this.buy(id, b, true), buyGems: (id, b) => void this.buyGems(id, b, true), name: (id) => this.view(id)?.name ?? "", sandbox: this.l.storeKind === "sandbox" });
  }

  close(): void { window.clearInterval(this.timer); this.el.remove(); }

  /** A tile's name and what it is, in a bubble over it (a tap elsewhere closes it). */
  private info(tile: HTMLElement): void {
    this.el.querySelector(".dinfo")?.remove();
    const box = tile.getBoundingClientRect(), host = this.el.getBoundingClientRect();
    const tip = document.createElement("div");
    tip.className = "dinfo";
    tip.innerHTML = `<b>${tile.dataset.name ?? ""}</b><small>${tile.dataset.detail ?? ""}</small>`;
    this.el.append(tip);
    const w = tip.offsetWidth || 220;
    const x = Math.max(8, Math.min(host.width - w - 8, box.left - host.left + box.width / 2 - w / 2));
    tip.style.left = `${x}px`;
    tip.style.top = `${Math.max(8, box.top - host.top - (tip.offsetHeight || 60) - 8)}px`;
    const off = (e: Event) => { if (!tip.contains(e.target as Node)) { tip.remove(); this.el.removeEventListener("click", off, true); } };
    window.setTimeout(() => this.el.addEventListener("click", off, true), 0);
  }

  // ------------------------------------------------------------------ choices

  private async pick(offerId: string, slot: number): Promise<void> {
    const v = this.view(offerId), def = offerOf(this.balance.offers, offerId);
    if (!v || !def) return;
    const choice = await choosePick(this.parent, this.balance, this.l.deals.level, v.choices, v.picks[slot] ?? -1, this.names, slot);
    if (choice === null) return;
    const r = this.l.deals.setPick(offerId, slot, choice);
    if (r !== true) { toast(this.parent, blockedText(r)); return; }
    this.l.changed();
    const item = v.choices[choice];
    if (item.kind === "gear" && item.slot === "pick") {
      // its slot index: the gear picks among the fixed items, then those in earlier pick slots
      const after = this.view(offerId)!;
      let gi = gearPicks(def.items);
      for (let s = 0; s < slot; s++) { const c = after.picks[s]; if (c >= 0) gi += gearPicks([after.choices[c]]); }
      await this.gearSlot(offerId, gi, item.rarity);
    }
    this.render();
  }

  private async prize(offerId: string, i: number): Promise<void> {
    const v = this.view(offerId), def = offerOf(this.balance.offers, offerId);
    if (!v || !def) return;
    if (v.prize !== i) {
      const r = this.l.deals.setPrize(offerId, i);
      if (r !== true) { toast(this.parent, blockedText(r)); return; }
      econCue("tap");
      this.l.changed();
    }
    const item = v.prizes[i];
    if (item.kind === "gear" && item.slot === "pick") await this.gearSlot(offerId, gearPicks(def.items), item.rarity);
    this.render();
  }

  private async gearSlot(offerId: string, index: number, rarity: number): Promise<void> {
    const cur = this.view(offerId)?.gearSlots[index] ?? -1;
    const s = await chooseGearSlot(this.parent, this.balance, rarity, cur);
    if (s === null) return;
    const r = this.l.deals.setGearSlot(offerId, index, s);
    if (r !== true) toast(this.parent, blockedText(r));
    else this.l.changed();
  }

  private async odds(offerId: string): Promise<void> {
    const v = this.view(offerId);
    if (!v) return;
    // every random thing the offer can pay: what it holds now, and each choice or prize not yet chosen
    await showOdds(this.parent, this.balance, this.l.deals.level, v.name, [...v.items, ...v.choices, ...v.prizes], this.names);
  }

  private async changeTheme(p: PageView): Promise<void> {
    const t = p.theme, d = p.page.daily;
    if (!t || !d) return;
    if (t.changesLeft <= 0) {
      toast(this.parent, this.l.deals.state.dailyBoughtToday(this.l.now()) ? "Today's deals are set: you bought one. New ones tomorrow" : "No changes left today. More with VIP 5");
      return;
    }
    const i = await chooseTheme(this.parent, this.balance, this.l.deals.level, d.themes, t.id, this.names);
    if (i === null) return;
    const r = this.l.deals.changeTheme(d.themes[i].id, this.l.now());
    if (r !== true) { toast(this.parent, blockedText(r)); return; }
    econCue("tap");
    this.l.changed();
    this.render();
    purchasePop(this.el.querySelector(".dbody"));
  }

  private milestone(tokens: number, stop: HTMLElement): void {
    if (!stop.classList.contains("s-ready")) {
      const t = pageOf(this.balance.offers, "weekly")?.track.find((m) => m.tokens === tokens);
      const names = t ? itemTiles(this.balance, t.items, this.l.deals.level, [], this.names).map((x) => x.name).join(", ") : "";
      toast(this.parent, stop.classList.contains("s-claimed") ? `Claimed this week: ${names}` : `${tokens} Weekly Tokens: ${names}, free`);
      return;
    }
    const r = this.l.deals.claimMilestone(tokens, this.l.now());
    if (typeof r === "string") { toast(this.parent, blockedText(r)); return; }
    this.delivered("FREE REWARD", r, [], stop);
  }

  // ------------------------------------------------------------------ buying

  /** `confirmed`: BUY in the pack popup, which was the test store's confirm. */
  private async buy(offerId: string, btn: HTMLButtonElement, confirmed = false): Promise<void> {
    if (this.busy) return;
    const v = this.view(offerId);
    if (!v) { toast(this.parent, "That offer just ended"); this.render(); return; }
    if (v.blocked) {
      if (needsChoice(v.blocked)) { this.firstChoice(offerId, v); return; }
      toast(this.parent, blockedText(v.blocked));
      return;
    }
    if (this.l.storeKind === "sandbox" && !confirmed) {
      // The platform store confirms a real payment itself; the web's test store gets one confirm so a stray tap buys nothing.
      const ok = await confirmPurchase(this.parent, { shop: true,
        title: "TEST PURCHASE", currency: null, price: priceText(v.price, this.prices), confirm: "BUY", className: "dconfirm",
        body: `<p class="dcname"><b>${v.name}</b></p><div class="dctiles">${wellsOf(itemTiles(this.balance, v.items, this.l.deals.level, v.gearSlots, this.names), "sm", "span")}</div><p>Test build: nothing is charged.</p>`,
      });
      if (!ok) return;
    }
    this.busy = true;
    btn.classList.add("busy");
    btn.disabled = true;
    const card = btn.closest<HTMLElement>(".doffer");
    try {
      const r = await this.l.buy(offerId);
      // a ladder page moves on to its next pack once one is bought (§18)
      if (r.status === "granted" && this.page) this.ladderAt.delete(this.page);
      if (r.status === "granted") this.delivered(v.name.toUpperCase(), r.granted, r.bankedGear, card);
      else if (r.status === "pending") toast(this.parent, "Awaiting approval: it arrives the moment it's given");
      else if (r.status === "refused" || r.status === "failed") toast(this.parent, purchaseFailText(r.reason));
    } finally {
      this.busy = false;
      if (this.el.isConnected) this.render();
    }
  }

  /** A blocked "Choose your picks" button opens the first empty choice. */
  private firstChoice(offerId: string, v: OfferView): void {
    const empty = v.picks.findIndex((p) => p < 0);
    if (empty >= 0) { void this.pick(offerId, empty); return; }
    if (v.prizes.length > 0 && v.prize < 0) {
      const card = this.el.querySelector<HTMLElement>(`.doffer[data-offer="${offerId}"] .prizes`);
      card?.scrollIntoView({ block: "center", behavior: "smooth" });
      purchasePop(card ?? null);
      toast(this.parent, "Choose your prize first");
      return;
    }
    // a gear slot is missing: the first gear pick without one
    const def = offerOf(this.balance.offers, offerId);
    const rarity = [...v.items].find((i): i is Extract<OfferItem, { kind: "gear" }> => i.kind === "gear" && i.slot === "pick")?.rarity ?? 0;
    if (def) void this.gearSlot(offerId, v.gearSlots.length, rarity).then(() => this.render());
  }

  private async buyGems(packId: string, btn: HTMLButtonElement, confirmed = false): Promise<void> {
    const pack = this.l.gemPacks().find((p) => p.id === packId);
    if (!pack || this.busy) return;
    if (this.l.storeKind === "sandbox" && !confirmed) {
      const ok = await confirmPurchase(this.parent, { shop: true, title: "TEST PURCHASE", currency: null, price: this.prices.get(pack.id) ?? pack.referencePrice, confirm: "BUY",
        body: `<p class="egems">${gemIcon(22)} <b>${fmtCompact(economyLink()?.purchases?.firstBonus(pack.id) ? pack.gems * 2 : pack.gems)} gems</b></p><p>Test build: nothing is charged.</p>` });
      if (!ok) return;
    }
    this.busy = true;
    btn.classList.add("busy");
    btn.disabled = true;
    const before = this.l.catalog.economy.wallet.gems, card = btn.closest(".dgem");
    try {
      const r = await this.l.buyGems(packId);
      if (r.status === "granted" || r.status === "duplicate") {
        econCue("buy");
        this.l.changed();
        purchasePop(card);
        flyCurrency(card, "gems", before, this.l.catalog.economy.wallet.gems);
        if (r.status === "granted") toast(this.parent, `+${fmtCompact(r.gems)} gems. Thank you, Commander!`);
      } else if (r.status === "pending") toast(this.parent, "Awaiting approval: the gems arrive the moment it's given");
      else if (r.status === "refused" || r.status === "failed") toast(this.parent, purchaseFailText(r.reason));
    } finally {
      this.busy = false;
      if (this.el.isConnected) this.render();
    }
  }

  /** The receipt: the cue, the card's glow, gems flying to the pill, and what arrived (rolled chests by rarity). */
  private delivered(title: string, g: GrantedItems, bankedGear: readonly GearItem[], card: Element | null): void {
    econCue("buy");
    const before = this.l.catalog.economy.wallet.gems - g.gems;
    this.l.changed();
    purchasePop(card);
    if (g.gems > 0) flyCurrency(card, "gems", before, this.l.catalog.economy.wallet.gems);
    window.setTimeout(() => { if (this.el.isConnected) this.render(); }, REVEAL_MS);
    revealThenReceipt(this.parent, title, g, bankedGear);
  }
}

function gearPicks(items: readonly OfferItem[]): number {
  return items.reduce((n, i) => n + (i.kind === "gear" && i.slot === "pick" ? i.count : 0), 0);
}

function skinName(id: string): string {
  const last = id.split(".").pop() ?? id;
  return last.charAt(0).toUpperCase() + last.slice(1);
}
